import type { RefAttributes } from "react";
import { useMemo } from "react";
import type { TextElementType, TextProps } from "../Text/Text";
import { Text } from "../Text/Text";

export type HTMLElementType = "span" | "div";

export interface TextOrHTMLProps
  extends Omit<TextProps, "children">, RefAttributes<TextElementType> {
  /** HTML string to be rendered with dangerouslySetInnerHTML */
  text: string;
  /** Custom class name for the dangerous HTML element */
  htmlClassName?: string;
  /** HTML element type for the dangerous HTML element */
  htmlElementType?: HTMLElementType;
  /** If true, wraps the dangerous HTML element inside a Text component */
  textWrapper?: boolean;
}

const TextOrHTML = ({
  ref,
  text,
  htmlClassName,
  htmlElementType = "span",
  textWrapper = false,
  ...textProps
}: TextOrHTMLProps) => {
  const DangerousComponent = htmlElementType;

  // Memoize the dangerouslySetInnerHTML object to prevent unnecessary re-renders
  // See: https://github.com/facebook/react/issues/31660
  const dangerousHTML = useMemo(() => ({ __html: text }), [text]);

  const dangerousElement = (
    <DangerousComponent
      className={htmlClassName}
      dangerouslySetInnerHTML={dangerousHTML}
    />
  );

  if (textWrapper) {
    return (
      <Text ref={ref} {...textProps}>
        {dangerousElement}
      </Text>
    );
  }

  return dangerousElement;
};

TextOrHTML.displayName = "TextOrHTML";
export { TextOrHTML };
